import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { InlineError } from '@/components/ui/inline-error';


interface Person {
  id: number;
  name: string;
  profile_path: string | null;
  character?: string;
  job?: string;
}

interface CastCrewData {
  cast: Person[];
  crew: {
    directors: Person[];
    writers: Person[];
    producers: Person[];
  };
}

interface CrewScore {
  name: string;
  avg_rating: number;
  film_count: number;
}

interface CastCrewProps {
  tmdbId: number;
  mediaType: 'movie' | 'tv';
  crewScores?: {
    actors: CrewScore[];
    director: CrewScore | null;
    producers: CrewScore[];
  };
}

export const CastCrew = ({ tmdbId, mediaType, crewScores }: CastCrewProps) => {
  const [data, setData] = useState<CastCrewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchCastCrew = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const { data: castData, error } = await supabase.functions.invoke('tmdb-cast', {
        body: { tmdbId, mediaType }
      });

      if (error) throw error;
      setData(castData);
    } catch (error) {
      console.error('Error fetching cast/crew:', error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [tmdbId, mediaType]);

  useEffect(() => {
    fetchCastCrew();
  }, [fetchCastCrew]);

  // Find crew score by name
  const getActorScore = (name: string): CrewScore | undefined => {
    return crewScores?.actors.find(a => a.name === name);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Cast & Crew
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (failed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Cast & Crew
          </CardTitle>
        </CardHeader>
        <CardContent>
          <InlineError message="We couldn't pull the cast list for this title." onRetry={fetchCastCrew} />
        </CardContent>
      </Card>
    );
  }

  if (!data || (!data.cast?.length && !data.crew?.directors?.length && !data.crew?.writers?.length && !data.crew?.producers?.length)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Cast & Crew
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No cast or crew credits are listed for this title yet.
          </p>
        </CardContent>
      </Card>
    );
  }


  const { cast, crew } = data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Cast & Crew
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Key Crew with Reputation Scores */}
        {(crew.directors.length > 0 || crew.writers.length > 0 || (crewScores?.producers?.length ?? 0) > 0) && (
          <div className="space-y-3">
            {crew.directors.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">
                  {crew.directors.length > 1 ? 'Directors' : 'Director'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {crew.directors.map((person) => (
                    <div key={person.id} className="flex items-center gap-1.5">
                      <Badge variant="secondary">
                        {person.name}
                      </Badge>
                      {crewScores?.director?.name === person.name && (
                        <span className="inline-flex items-center gap-0.5 text-xs font-medium text-warning">
                          <Star className="h-3 w-3 fill-current" />
                          {crewScores.director.avg_rating} avg · {crewScores.director.film_count} films
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {crew.writers.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">
                  {crew.writers.length > 1 ? 'Writers' : 'Writer'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {crew.writers.slice(0, 3).map((person) => (
                    <Badge key={person.id} variant="secondary">
                      {person.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {crewScores?.producers && crewScores.producers.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">
                  {crewScores.producers.length > 1 ? 'Producers' : 'Producer'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {crewScores.producers.map((prod, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <Badge variant="secondary">{prod.name}</Badge>
                      <span className="inline-flex items-center gap-0.5 text-xs font-medium text-warning">
                        <Star className="h-3 w-3 fill-current" />
                        {prod.avg_rating} avg · {prod.film_count} films
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Cast with Reputation Scores */}
        {cast.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium">Top Cast</p>
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex gap-4 pb-4">
                {cast.map((person) => {
                  const actorScore = getActorScore(person.name);
                  return (
                    <div key={person.id} className="flex-shrink-0 w-24">
                      <div className="aspect-[2/3] rounded-lg overflow-hidden bg-muted mb-2">
                        {person.profile_path ? (
                          <img
                            src={`https://image.tmdb.org/t/p/w185${person.profile_path}`}
                            alt={person.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Users className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs font-medium line-clamp-2">{person.name}</p>
                      {person.character && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {person.character}
                        </p>
                      )}
                      {actorScore && (
                        <p className="text-xs text-warning flex items-center gap-0.5 mt-0.5">
                          <Star className="h-2.5 w-2.5 fill-current" />
                          {actorScore.avg_rating} · {actorScore.film_count} films
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
